import type {
  CrmFilterCrm,
  CrmFilterSections,
  SearchFilters,
} from '@/types/search-filters';
import {
  COMPANY_TYPES,
  HEADCOUNT_RANGES,
  type CompanySearchFilters,
} from '@/utils/company-search.utils';
import { SENIORITY_LEVELS } from '@/constants/seniority-levels';
import { COMPANY_SIZE_VALUES } from '@/constants/company-size-buckets';
import { LINKEDIN_FUNCTIONS } from '@/constants/linkedin-functions';
import { INDUSTRIES } from '@/constants/industries';
import { INDUSTRY_GROUP_NAMES } from '@/constants/industry-groups';
import { COUNTRIES } from '@/constants/countries';
import { COUNTRY_GROUP_NAMES } from '@/constants/country-groups';
import { normalizeDomain } from '@/lib/normalize-domain';
import { FUNDING_ROUND_OPTIONS } from '@/constants/crustdata-funding-rounds';
import { migrateSearchFilters } from '@/utils/legacy-search-migration';

/**
 * Maps the Copilot's interpreted people-search parameters onto the
 * People-side ``SearchFilters`` shape so a natural-language query (typed on
 * the search landing OR handed off from the in-chat ``people_search`` tool)
 * populates the filter sidebar and runs natively on the page.
 *
 * The agent's ``people_search`` tool (``cleanse_api/agent/toolkits/
 * crustdata.py``) interprets NL into a curated Tier-1 param set and echoes
 * them back in the tool result's ``search_params`` block. This mapper is the
 * single translation + validation layer between that untrusted agent output
 * and the page's filter state.
 *
 * Two hard rules:
 *  1. **Normalize or drop.** Enum-like values (seniority, company size,
 *     function, industry) are validated against the canonical frontend vocab
 *     constants. A value that doesn't match is normalized to its canonical
 *     form or dropped — NEVER passed through verbatim. A hallucinated
 *     ``seniority: "wizard"`` or ``company_size: "medium"`` must not reach
 *     filter state and silently corrupt the page.
 *
 *     **Country is the one exception**, and deliberately so: those vocabs are
 *     closed sets, but ``COUNTRIES`` is a curated ~50-row list, so a perfectly
 *     real country outside it ("Peru") would be dropped. A dropped country
 *     removes the predicate and BROADENS the audience invisibly, while an
 *     unmatched token returns zero rows — visible and correctable. Unrecognized
 *     countries are therefore preserved trimmed, matching what
 *     ``expandCountrySelection`` does at the wire boundary.
 *  2. **Back-compat.** The Phase-1 singular params (``job_title``,
 *     ``industry``, ``company_size``, ``company_name``, ``location``,
 *     ``seniority``) are still accepted — both for the old tool shape and for
 *     replaying historical chat results — and map identically for valid
 *     values.
 *
 * Dropped values + unmapped keys are logged in dev so coverage gaps and
 * model drift are visible rather than silent.
 */

type StrOrArr = string | string[] | null;

export interface AgentSearchParams {
  // ── Legacy (Phase 1) singular params ──
  job_title?: string | null;
  industry?: string | null;
  company_size?: string | null;
  company_name?: string | null;
  location?: string | null;
  seniority?: StrOrArr;

  // ── Tier-1: job / title ──
  job_titles_include?: StrOrArr;
  job_titles_exclude?: StrOrArr;
  job_title_match_type?: string | null;
  title_boolean_query?: string | null;
  keywords_boolean_query?: string | null;

  // ── Tier-1: function ──
  function_categories?: StrOrArr;
  past_function_categories?: StrOrArr;

  // ── Tier-1: company attributes ──
  industries_include?: StrOrArr;
  industries_exclude?: StrOrArr;
  company_sizes?: StrOrArr;
  current_company_domains?: StrOrArr;

  // ── Tier-1: seniority mirror ──
  past_seniority?: StrOrArr;

  // ── Tier-1: company history / exclusions ──
  past_companies?: StrOrArr;
  never_worked_at_companies?: StrOrArr;

  // ── Tier-1: location ──
  contact_countries?: StrOrArr;
  contact_regions?: StrOrArr;
  company_countries?: StrOrArr;

  // ── Tier-1: signals ──
  recently_changed_jobs?: boolean | string | number | null;

  // ── Company slice (CompanyDB firmographics → composite) ──
  funding_round_types?: StrOrArr;
  funding_round_types_exclude?: StrOrArr;
  revenue_min?: number | string | null;
  revenue_max?: number | string | null;
  valuation_min?: number | string | null;
  valuation_max?: number | string | null;
  employee_growth_min?: number | string | null;
  employee_count_min?: number | string | null;
  employee_count_max?: number | string | null;
  year_founded_min?: number | string | null;
  year_founded_max?: number | string | null;
  last_funding_after?: string | null;
  ipo_after?: string | null;
  bootstrapped_only?: boolean | string | null;
  acquired_only?: boolean | string | null;

  // Non-filter / pagination — ignored.
  limit?: number | null;
  page?: number | null;
}

// Existing People Search exclusion fields can arrive from saved/copilot
// handoffs in native or snake_case form. Preserve them through the same
// migration used by saved searches, including unsupported inclusion clauses
// so the search boundary can show their validation error.
const EXCLUSION_KEYS = [
  'excludeCrmCustomers',
  'excludeCrmActiveOpportunities',
  'excludeCrmOwnedByMe',
  'excludeCrmOwnedByOthers',
  'excludeCrmOwner',
  'excludeCrmAccountStatus',
  'crmFilterSections',
  'excludeInCleanlist',
  'excludeInCleanlistListIds',
  // Clu-only "In Instantly": the search Clu ran skipped these people, so the
  // page must open with the option set.
  'excludeInInstantly',
  'excludeInInstantlyStatuses',
  'excludeInInstantlyBlocklist',
  'excludeProfileUrls',
  'excludeCompanyDomains',
] as const;
const snakeKey = (key: string) =>
  key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

// people_search's ``crm_exclusions``: ``[{crm, condition}]`` with the
// /crm/exclusion-set tokens the page's CRM sections resolve
// (``current_customers``, ``owned_any``, ``in_crm``, …). ``active_outreach``
// is the chat search's own (recent CRM Tasks); no page section applies it.
// Attio has no section on the search page, so an Attio clause is the chat's
// too: it never opens there as if it were applied.
const CRM_EXCLUSION_CRMS: readonly CrmFilterCrm[] = ['salesforce', 'hubspot'];
const CHAT_ONLY_CRM_CONDITIONS = new Set(['active_outreach']);

/** Why a search with an Attio exclusion doesn't open on the search page. */
export const ATTIO_SEARCH_PAGE_REFUSAL =
  "the search page can't apply Attio exclusions yet, only Salesforce and HubSpot ones.";

/** The Attio clauses in a handoff's native `crmFilterSections` (either
 *  spelling), which the page's section sanitizer would drop. */
function attioSectionClauses(params: Record<string, unknown>): string[] {
  const sections = params.crmFilterSections ?? params.crm_filter_sections;
  if (!sections || typeof sections !== 'object') return [];
  const clauses = (sections as Record<string, unknown>).attio;
  if (!Array.isArray(clauses)) return [];
  return clauses.map((clause) => {
    const condition =
      typeof clause === 'string'
        ? clause
        : clause && typeof clause === 'object'
          ? (clause as Record<string, unknown>).condition
          : undefined;
    // A malformed clause still counts: it is an Attio exclusion all the same.
    const word = typeof condition === 'string' ? condition.trim() : '';
    return `attio:${word || 'unknown'}`;
  });
}

function crmExclusionList(
  value: unknown
): { crm: CrmFilterCrm | 'attio'; condition: string }[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const { crm, condition } = item as Record<string, unknown>;
    // A malformed Attio clause still names Attio, so it still keeps the
    // search off the page.
    if (crm === 'attio' && (typeof condition !== 'string' || !condition.trim()))
      return [{ crm: 'attio' as const, condition: 'unknown' }];
    if (
      (crm !== 'attio' && !CRM_EXCLUSION_CRMS.includes(crm as CrmFilterCrm)) ||
      typeof condition !== 'string' ||
      !condition.trim()
    ) {
      return [];
    }
    return [
      { crm: crm as CrmFilterCrm | 'attio', condition: condition.trim() },
    ];
  });
}

/** CRM exclusions in a people_search's params the search page can't apply
 *  ("not being outreached", any Attio clause): a result with one keeps its
 *  rows in the chat, where the exclusion was applied, instead of opening the
 *  page without it. */
export function crmExclusionsOnlyTheChatApplies(
  params: Record<string, unknown> | null | undefined
): string[] {
  if (!params) return [];
  return [
    ...crmExclusionList(params.crm_exclusions)
      .filter(
        ({ crm, condition }) =>
          crm === 'attio' || CHAT_ONLY_CRM_CONDITIONS.has(condition)
      )
      .map(({ crm, condition }) => `${crm}:${condition}`),
    ...attioSectionClauses(params),
  ];
}

// Every key the mapper knows about. Anything outside this set is dev-logged
// as an unmapped param so we notice when the agent starts emitting something
// new (a Tier-2 promotion candidate).
const KNOWN_KEYS = new Set<string>([
  ...EXCLUSION_KEYS,
  ...EXCLUSION_KEYS.map(snakeKey),
  'crm_exclusions',
  'instantly_exclusion',
  'job_title',
  'industry',
  'company_size',
  'company_name',
  'location',
  'seniority',
  'job_titles_include',
  'job_titles_exclude',
  'job_title_match_type',
  'title_boolean_query',
  'keywords_boolean_query',
  'function_categories',
  'past_function_categories',
  'industries_include',
  'industries_exclude',
  'company_sizes',
  'current_company_domains',
  'past_seniority',
  'past_companies',
  'never_worked_at_companies',
  'contact_countries',
  'contact_regions',
  'company_countries',
  'recently_changed_jobs',
  // company slice (mapped by mapAgentSearchParamsToCompanyFilters)
  'funding_round_types',
  'funding_round_types_exclude',
  'revenue_min',
  'revenue_max',
  'valuation_min',
  'valuation_max',
  'employee_growth_min',
  'employee_count_min',
  'employee_count_max',
  'year_founded_min',
  'year_founded_max',
  'last_funding_after',
  'ipo_after',
  'bootstrapped_only',
  'acquired_only',
  'limit',
  'page',
]);

// ─── Canonical lookup tables (built once) ───────────────────────────

/** lower(value) -> canonical value, for case-insensitive membership. */
function ciMap(values: readonly string[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const v of values) m.set(v.toLowerCase(), v);
  return m;
}

const SENIORITY_CANON = ciMap(SENIORITY_LEVELS);
const FUNCTION_CANON = ciMap(LINKEDIN_FUNCTIONS);
const INDUSTRY_CANON = ciMap(INDUSTRIES);
const INDUSTRY_GROUP_CANON = ciMap(INDUSTRY_GROUP_NAMES);
const COUNTRY_GROUP_CANON = ciMap(COUNTRY_GROUP_NAMES);

// lower(display | full | iso3) -> canonical full name. The People country
// state holds full names + group tokens (what the picker writes), and
// buildCrustdataFilters' expandCountrySelectionToFullNames resolves any form
// to the PersonDB full-name match. Emitting the full name keeps a
// handoff-applied country identical to a manually-picked one, so the picker
// highlights it as selected.
const COUNTRY_TO_FULL = (() => {
  const m = new Map<string, string>();
  for (const c of COUNTRIES) {
    m.set(c.iso3.toLowerCase(), c.full);
    m.set(c.full.toLowerCase(), c.full);
    m.set(c.display.toLowerCase(), c.full);
  }
  return m;
})();

// Seniority synonyms the agent (or a user phrase the agent echoes) might emit
// that aren't the exact canonical label. Every right-hand value MUST be a
// member of SENIORITY_LEVELS.
const SENIORITY_SYNONYMS: Record<string, string> = {
  owner: 'Owner / Partner',
  founder: 'Owner / Partner',
  cofounder: 'Owner / Partner',
  'co-founder': 'Owner / Partner',
  'co founder': 'Owner / Partner',
  partner: 'Owner / Partner',
  'c suite': 'CXO',
  'c-suite': 'CXO',
  csuite: 'CXO',
  'c level': 'CXO',
  'c-level': 'CXO',
  cxo: 'CXO',
  chief: 'CXO',
  ceo: 'CXO',
  cto: 'CXO',
  cfo: 'CXO',
  coo: 'CXO',
  cmo: 'CXO',
  cio: 'CXO',
  cro: 'CXO',
  cpo: 'CXO',
  vp: 'Vice President',
  'vice president': 'Vice President',
  svp: 'Vice President',
  evp: 'Vice President',
  head: 'Director',
  director: 'Director',
  manager: 'Manager',
  senior: 'Senior',
  entry: 'Entry',
  intern: 'Entry',
  trainee: 'Training',
  training: 'Training',
};

// ─── Per-field normalizers (return canonical value or null = drop) ──

function normalizeSeniority(v: string): string | null {
  const key = v.trim().toLowerCase();
  if (!key) return null;
  return SENIORITY_SYNONYMS[key] ?? SENIORITY_CANON.get(key) ?? null;
}

function normalizeCompanySize(v: string): string | null {
  // Compare comma-insensitively so "501-1000" maps to the canonical
  // "501-1,000". Anything that isn't a real bucket ("medium") drops.
  const stripped = v.replace(/,/g, '').trim();
  if (!stripped) return null;
  const match = COMPANY_SIZE_VALUES.find(
    (val) => val.replace(/,/g, '') === stripped
  );
  return match ?? null;
}

// The provider's spelling and the page's older label for Customer Success,
// which saved chats and searches still carry (the API reads them the same).
const FUNCTION_ALIASES: ReadonlyMap<string, string> = new Map([
  ['customer success and support', 'Customer Success'],
  ['support', 'Customer Success'],
]);

function normalizeFunction(v: string): string | null {
  const key = v.trim().toLowerCase();
  return FUNCTION_CANON.get(key) ?? FUNCTION_ALIASES.get(key) ?? null;
}

// The functions the provider stopped taking on Sep 29 ("Support" is read as
// Customer Success above). The mapper drops them because they match no one,
// so an older Clu search that held only these would open with no function
// filter at all, wider than asked. The page says so instead.
const RETIRED_FUNCTIONS = new Set([
  'information technology',
  'business development',
  'education',
  'healthcare services',
  'media and communication',
  'administrative',
  'community and social services',
  'real estate',
  'quality assurance',
  'accounting',
  'program and project management',
  'purchasing',
  'entrepreneurship',
  'military and protective services',
]);

export const RETIRED_FUNCTIONS_NOTICE =
  'Some job functions from this search are no longer offered';

/** True when the params name a job function the mapper drops as retired. */
export function hasRetiredFunctions(
  params: AgentSearchParams | Record<string, unknown> | null | undefined
): boolean {
  if (!params || typeof params !== 'object') return false;
  const p = params as AgentSearchParams;
  return [
    ...toStringArray(p.function_categories),
    ...toStringArray(p.past_function_categories),
  ].some((v) => RETIRED_FUNCTIONS.has(v.toLowerCase()));
}

function normalizeIndustry(v: string): string | null {
  const key = v.trim().toLowerCase();
  if (!key) return null;
  // A group token ("Software & IT") wins — buildCrustdataFilters expands it
  // via expandIndustrySelection into its member LinkedIn industries.
  // Otherwise require an exact canonical industry. A near-miss like "Software"
  // (canonical is "Software Development") drops rather than producing a
  // zero-result ``in`` match.
  return INDUSTRY_GROUP_CANON.get(key) ?? INDUSTRY_CANON.get(key) ?? null;
}

// Preserves an unrecognized token instead of dropping it — see rule 1 above.
// Only a blank drops, because a blank carries no predicate to lose.
function normalizeCountry(v: string): string | null {
  const trimmed = v.trim();
  if (!trimmed) return null;
  const key = trimmed.toLowerCase();
  return COUNTRY_GROUP_CANON.get(key) ?? COUNTRY_TO_FULL.get(key) ?? trimmed;
}

function normalizeMatchType(
  v: string
): SearchFilters['jobTitleMatchType'] | null {
  const key = v.trim().toLowerCase();
  if (key === 'contains' || key === 'exact' || key === 'similar') return key;
  return null;
}

// ─── Small helpers ──────────────────────────────────────────────────

function toStringArray(v: StrOrArr | undefined): string[] {
  if (v == null) return [];
  const arr = Array.isArray(v) ? v : [v];
  const out: string[] = [];
  for (const item of arr) {
    if (typeof item !== 'string') continue;
    const t = item.trim();
    if (t) out.push(t);
  }
  return out;
}

function dedupe(arr: string[]): string[] {
  return Array.from(new Set(arr));
}

/** Map each value through a normalizer; keep canonical hits, record drops. */
function normalizeArray(
  raw: string[],
  normalizer: (v: string) => string | null,
  field: string,
  dropped: string[]
): string[] {
  const out: string[] = [];
  for (const item of raw) {
    const n = normalizer(item);
    if (n) out.push(n);
    else dropped.push(`${field}="${item}"`);
  }
  return dedupe(out);
}

function coerceBool(v: unknown): boolean {
  if (v === true) return true;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') {
    const t = v.trim().toLowerCase();
    return t === 'true' || t === 'yes' || t === '1';
  }
  return false;
}

/** The In Instantly keys nested under ``instantly_exclusion``, or {}. With
 *  statuses and no mode the mode is statuses, as the API reads it
 *  (crm_exclusions.instantly_reference). */
function nestedInstantlyKeys(params: Record<string, unknown>) {
  const value = params.instantly_exclusion;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const nested = value as Record<string, unknown>;
  const mode = nested.excludeInInstantly ?? nested.exclude_in_instantly;
  const statuses =
    nested.excludeInInstantlyStatuses ?? nested.exclude_in_instantly_statuses;
  return mode === undefined && Array.isArray(statuses) && statuses.length
    ? { ...nested, excludeInInstantly: 'statuses' }
    : nested;
}

// ─── Mapper ─────────────────────────────────────────────────────────

export function mapAgentSearchParamsToFilters(
  params: AgentSearchParams | Record<string, unknown> | null | undefined
): Partial<SearchFilters> {
  if (!params || typeof params !== 'object') return {};
  const p = params as AgentSearchParams;
  const out: Partial<SearchFilters> = {};
  const dropped: string[] = [];
  // people_search nests In Instantly under ``instantly_exclusion`` in both
  // its args and its search_params (camel or snake keys, as the API takes).
  const nested = nestedInstantlyKeys(params as Record<string, unknown>);
  const input = { ...(params as Record<string, unknown>), ...nested };
  const exclusionFields = Object.fromEntries(
    EXCLUSION_KEYS.filter((key) => key in input || snakeKey(key) in input).map(
      (key) => [key, input[key] ?? input[snakeKey(key)]]
    )
  );
  const migrated = migrateSearchFilters(exclusionFields);
  Object.assign(
    out,
    Object.fromEntries(
      Object.keys(exclusionFields).map((key) => [
        key,
        migrated[key as keyof SearchFilters],
      ])
    )
  );

  // ── CRM exclusions: each opens as an exclude clause in its CRM's section ──
  if (Array.isArray(input.crm_exclusions)) {
    const sections: CrmFilterSections = {
      salesforce: [...(out.crmFilterSections?.salesforce ?? [])],
      hubspot: [...(out.crmFilterSections?.hubspot ?? [])],
    };
    for (const { crm, condition } of crmExclusionList(input.crm_exclusions)) {
      if (crm === 'attio' || CHAT_ONLY_CRM_CONDITIONS.has(condition)) {
        dropped.push(`crm_exclusions=${crm}:${condition}`);
        continue;
      }
      if (sections[crm].some((clause) => clause.condition === condition)) {
        continue;
      }
      sections[crm].push({
        id: `clu-${crm}-${condition}`,
        polarity: 'exclude',
        condition,
      });
    }
    out.crmFilterSections = sections;
  }

  // ── Job titles (legacy job_title ∪ new job_titles_include) ──
  const titlesInc = dedupe([
    ...toStringArray(p.job_titles_include),
    ...toStringArray(p.job_title),
  ]);
  if (titlesInc.length) out.jobTitlesInclude = titlesInc;

  const titlesExc = dedupe(toStringArray(p.job_titles_exclude));
  if (titlesExc.length) out.jobTitlesExclude = titlesExc;

  if (typeof p.job_title_match_type === 'string') {
    const mt = normalizeMatchType(p.job_title_match_type);
    if (mt) out.jobTitleMatchType = mt;
    else if (p.job_title_match_type.trim())
      dropped.push(`job_title_match_type="${p.job_title_match_type}"`);
  }

  if (
    typeof p.title_boolean_query === 'string' &&
    p.title_boolean_query.trim()
  ) {
    out.titleBooleanQuery = p.title_boolean_query.trim();
  }
  if (
    typeof p.keywords_boolean_query === 'string' &&
    p.keywords_boolean_query.trim()
  ) {
    out.keywordsBooleanQuery = p.keywords_boolean_query.trim();
  }

  // ── Seniority (legacy string ∪ new array), validated ──
  const seniority = normalizeArray(
    toStringArray(p.seniority),
    normalizeSeniority,
    'seniority',
    dropped
  );
  if (seniority.length) out.seniority = seniority;

  const pastSeniority = normalizeArray(
    toStringArray(p.past_seniority),
    normalizeSeniority,
    'past_seniority',
    dropped
  );
  if (pastSeniority.length) out.pastSeniority = pastSeniority;

  // ── Function category, validated against LINKEDIN_FUNCTIONS ──
  const fn = normalizeArray(
    toStringArray(p.function_categories),
    normalizeFunction,
    'function_categories',
    dropped
  );
  if (fn.length) out.functionCategoriesInclude = fn;

  const pastFn = normalizeArray(
    toStringArray(p.past_function_categories),
    normalizeFunction,
    'past_function_categories',
    dropped
  );
  if (pastFn.length) out.pastFunctionCategoriesInclude = pastFn;

  // ── Industry (legacy industry ∪ new industries_include), validated ──
  const indInc = normalizeArray(
    dedupe([
      ...toStringArray(p.industries_include),
      ...toStringArray(p.industry),
    ]),
    normalizeIndustry,
    'industries_include',
    dropped
  );
  if (indInc.length) out.industriesInclude = indInc;

  const indExc = normalizeArray(
    toStringArray(p.industries_exclude),
    normalizeIndustry,
    'industries_exclude',
    dropped
  );
  if (indExc.length) out.industriesExclude = indExc;

  // ── Company headcount (legacy company_size ∪ new company_sizes) ──
  const sizes = normalizeArray(
    dedupe([
      ...toStringArray(p.company_sizes),
      ...toStringArray(p.company_size),
    ]),
    normalizeCompanySize,
    'company_sizes',
    dropped
  );
  if (sizes.length) out.companySizes = sizes;

  // ── Company name: one employer, matched exactly as Clu searches it, so
  // "Notion" does not also open "Notion Media Pro". Crustdata's exact match
  // on the employer name ignores case: "notion", "NOTION" and "Notion" return
  // the same people (checked live on /person/search, 2026-10-08) ──
  if (typeof p.company_name === 'string' && p.company_name.trim()) {
    out.companyNameFilter = p.company_name.trim();
    out.companyNameMatchType = 'exact';
  }

  // ── Current employer website domains ──
  const domains = normalizeArray(
    toStringArray(p.current_company_domains),
    normalizeDomain,
    'current_company_domains',
    dropped
  );
  if (domains.length) out.currentCompanyDomains = domains;

  // ── Past / excluded companies (free text) ──
  const pastCos = dedupe(toStringArray(p.past_companies));
  if (pastCos.length) out.pastCompanies = pastCos;

  const neverCos = dedupe(toStringArray(p.never_worked_at_companies));
  if (neverCos.length) out.neverWorkedAtCompanies = neverCos;

  // ── Location ──
  // Person country -> iso3 / group token (see COUNTRY_TO_ISO3 note).
  const contactCountries = normalizeArray(
    toStringArray(p.contact_countries),
    normalizeCountry,
    'contact_countries',
    dropped
  );
  if (contactCountries.length) out.contactCountries = contactCountries;

  // Person state / province / metro — free text, fuzzy-matched server-side.
  // Phase 1 mapped the legacy ``location`` string here; preserved.
  const contactRegions = dedupe([
    ...toStringArray(p.contact_regions),
    ...toStringArray(p.location),
  ]);
  if (contactRegions.length) out.contactRegions = contactRegions;

  // Company HQ country.
  const companyCountries = normalizeArray(
    toStringArray(p.company_countries),
    normalizeCountry,
    'company_countries',
    dropped
  );
  if (companyCountries.length) out.companyCountries = companyCountries;

  // ── Signals ──
  if (coerceBool(p.recently_changed_jobs)) out.recentlyChangedJobs = true;

  // ── Dev observability: surface coverage gaps + rejected junk ──
  if (import.meta.env.DEV) {
    const unmapped = Object.keys(params).filter((k) => !KNOWN_KEYS.has(k));
    if (unmapped.length > 0) {
      console.warn(
        '[agent-filters] unmapped people-search params from Copilot:',
        unmapped
      );
    }
    if (dropped.length > 0) {
      console.warn(
        '[agent-filters] dropped values that failed canonical validation:',
        dropped
      );
    }
  }

  return out;
}

// ─── Company-slice mapper (firmographics → CompanySearchFilters) ────
//
// These params force the search through the composite endpoint (CompanyDB
// resolve → PersonDB). PersonDB cannot filter funding / revenue / valuation /
// growth / year-founded / IPO / acquired, so they live on the company slice
// and the page routes them via detectCompositeRoute.

// lower(value | spaced value | label) -> canonical funding-round LABEL.
// The composite CompanySearchFilters.fundingRoundTypes slice stores the
// display LABEL ("Series B") — that is what the manual firmographics
// dropdown emits and what migrateFundingLabel / buildCompanyFilters
// validate against. Emitting the wire value ("series_b") here made the
// migrator drop every Copilot-supplied funding stage on URL hydration,
// so the composite route never fired (plain PersonDB search instead).
const FUNDING_CANON = (() => {
  const m = new Map<string, string>();
  for (const o of FUNDING_ROUND_OPTIONS) {
    m.set(o.value.toLowerCase(), o.label);
    m.set(o.value.replace(/_/g, ' '), o.label);
    m.set(o.label.toLowerCase(), o.label);
  }
  return m;
})();

function normalizeFundingRound(v: string): string | null {
  return FUNDING_CANON.get(v.trim().toLowerCase()) ?? null;
}

/** Numeric-ish input → trimmed numeric string (CompanySearchFilters numeric
 *  fields are strings). Tolerates "$10,000,000". null = drop. */
function toNumericString(v: number | string | null | undefined): string | null {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : null;
  const t = String(v).trim();
  if (!t) return null;
  const n = Number(t.replace(/[,$\s]/g, ''));
  return Number.isFinite(n) ? String(n) : null;
}

export function mapAgentSearchParamsToCompanyFilters(
  params: AgentSearchParams | Record<string, unknown> | null | undefined
): Partial<CompanySearchFilters> {
  if (!params || typeof params !== 'object') return {};
  const p = params as AgentSearchParams;
  const out: Partial<CompanySearchFilters> = {};
  const dropped: string[] = [];

  const fund = normalizeArray(
    toStringArray(p.funding_round_types),
    normalizeFundingRound,
    'funding_round_types',
    dropped
  );
  if (fund.length) out.fundingRoundTypes = fund;

  const fundExc = normalizeArray(
    toStringArray(p.funding_round_types_exclude),
    normalizeFundingRound,
    'funding_round_types_exclude',
    dropped
  );
  if (fundExc.length) out.fundingRoundTypesExclude = fundExc;

  const revMin = toNumericString(p.revenue_min);
  if (revMin) out.revenueMin = revMin;
  const revMax = toNumericString(p.revenue_max);
  if (revMax) out.revenueMax = revMax;
  const growth = toNumericString(p.employee_growth_min);
  if (growth) out.growthMin = growth;
  const headcountMin = toNumericString(p.employee_count_min);
  if (headcountMin) out.headcountLatestMin = headcountMin;
  const headcountMax = toNumericString(p.employee_count_max);
  if (headcountMax) out.headcountLatestMax = headcountMax;
  const yfMin = toNumericString(p.year_founded_min);
  if (yfMin) out.yearFoundedMin = yfMin;
  const yfMax = toNumericString(p.year_founded_max);
  if (yfMax) out.yearFoundedMax = yfMax;

  if (typeof p.last_funding_after === 'string' && p.last_funding_after.trim()) {
    out.lastFundingDateAfter = p.last_funding_after.trim();
  }
  if (typeof p.ipo_after === 'string' && p.ipo_after.trim()) {
    out.ipoDateAfter = p.ipo_after.trim();
  }
  if (coerceBool(p.bootstrapped_only)) out.bootstrappedOnly = true;
  if (coerceBool(p.acquired_only)) out.acquiredOnly = true;

  // Inverted-range guard. An agent can emit a min that exceeds its max
  // (e.g. revenue_min=999999999, revenue_max=100), which the composite
  // CompanyDB filter ANDs into an unsatisfiable condition → zero results
  // with no obvious cause. For every numeric min/max pair, swap the two
  // when min > max so the search stays meaningful. Values are stored as
  // numeric strings (see toNumericString); compare numerically.
  const swapIfInverted = (
    minKey: 'revenueMin' | 'yearFoundedMin' | 'headcountLatestMin',
    maxKey: 'revenueMax' | 'yearFoundedMax' | 'headcountLatestMax'
  ) => {
    const min = out[minKey];
    const max = out[maxKey];
    if (min == null || max == null) return;
    const minN = Number(min);
    const maxN = Number(max);
    if (Number.isNaN(minN) || Number.isNaN(maxN)) return;
    if (minN > maxN) {
      out[minKey] = max;
      out[maxKey] = min;
    }
  };
  swapIfInverted('revenueMin', 'revenueMax');
  swapIfInverted('yearFoundedMin', 'yearFoundedMax');
  swapIfInverted('headcountLatestMin', 'headcountLatestMax');

  if (import.meta.env.DEV && dropped.length > 0) {
    console.warn(
      '[agent-filters] dropped company-filter values that failed validation:',
      dropped
    );
  }

  return out;
}

// ─── company_search handoff (Find Companies tab) ────────────────────

const COMPANY_TYPE_CANON = ciMap(COMPANY_TYPES);
// Loose phrasings the agent might emit for CompanyDB company_type.
const COMPANY_TYPE_SYNONYMS: Record<string, string> = {
  private: 'Privately Held',
  'privately-held': 'Privately Held',
  public: 'Public Company',
  'publicly traded': 'Public Company',
  nonprofit: 'Non Profit',
  'non-profit': 'Non Profit',
  government: 'Government Agency',
  'self-employed': 'Self Employed',
  school: 'Educational Institution',
  university: 'Educational Institution',
};

// The Companies tab's headcount buckets carry NO comma ("501-1000"), unlike the
// People tab's ``companySizes`` ("501-1,000"): HEADCOUNT_BUCKET_BOUNDS only
// knows the comma-free spelling, so a comma value compiles to no condition and
// the filter the user asked for silently never applies. Take either spelling
// and emit the Companies tab's own.
function normalizeCompanyHeadcountRange(v: string): string | null {
  const stripped = v.replace(/,/g, '').trim();
  return (
    HEADCOUNT_RANGES.find((range) => range.value === stripped)?.value ?? null
  );
}

function normalizeCompanyType(v: string): string | null {
  const key = v.trim().toLowerCase();
  if (!key) return null;
  return COMPANY_TYPE_SYNONYMS[key] ?? COMPANY_TYPE_CANON.get(key) ?? null;
}

// The Companies tab stores HQ countries as iso3 (plus group tokens). Resolve
// names to iso3 so a handoff-applied country matches a manual pick; keep
// unrecognized tokens (same rule as normalizeCountry).
const COUNTRY_TO_ISO3 = (() => {
  const m = new Map<string, string>();
  for (const c of COUNTRIES) {
    m.set(c.iso3.toLowerCase(), c.iso3);
    m.set(c.full.toLowerCase(), c.iso3);
    m.set(c.display.toLowerCase(), c.iso3);
  }
  return m;
})();

function normalizeCountryIso3(v: string): string | null {
  const trimmed = v.trim();
  if (!trimmed) return null;
  const key = trimmed.toLowerCase();
  return COUNTRY_GROUP_CANON.get(key) ?? COUNTRY_TO_ISO3.get(key) ?? trimmed;
}

/** Agent company_search params as echoed in the tool result's search_params. */
export interface AgentCompanySearchParams extends AgentSearchParams {
  domain?: string | null;
  description_keywords?: string | null;
  headcount_ranges?: StrOrArr;
  headcount_ranges_exclude?: StrOrArr;
  headcount_min?: number | string | null;
  headcount_max?: number | string | null;
  company_types?: StrOrArr;
  company_types_exclude?: StrOrArr;
  hq_countries?: StrOrArr;
  hq_countries_exclude?: StrOrArr;
  hq_states?: StrOrArr;
  hq_cities?: StrOrArr;
}

/**
 * Maps the Copilot's company_search params onto the Find Companies tab's
 * CompanySearchFilters (twin of mapAgentSearchParamsToFilters).
 *
 * Kept separate from mapAgentSearchParamsToCompanyFilters, which feeds the
 * People tab's composite slice: teaching it industries / headcount would
 * flip ordinary people searches into the composite route.
 */
export function mapAgentCompanySearchParamsToCompanyFilters(
  params: AgentCompanySearchParams | Record<string, unknown> | null | undefined
): Partial<CompanySearchFilters> {
  if (!params || typeof params !== 'object') return {};
  const p = params as AgentCompanySearchParams;
  const out: Partial<CompanySearchFilters> = {
    ...mapAgentSearchParamsToCompanyFilters(p),
  };
  const dropped: string[] = [];

  if (typeof p.company_name === 'string' && p.company_name.trim()) {
    out.companyName = p.company_name.trim();
  }
  if (typeof p.domain === 'string') {
    const d = normalizeDomain(p.domain);
    if (d) out.domain = d;
    else if (p.domain.trim()) dropped.push(`domain="${p.domain}"`);
  }
  if (
    typeof p.description_keywords === 'string' &&
    p.description_keywords.trim()
  ) {
    out.descriptionKeyword = p.description_keywords.trim();
  }

  // Industry (legacy singular ∪ plural), validated against the LinkedIn vocab.
  const indInc = normalizeArray(
    dedupe([
      ...toStringArray(p.industries_include),
      ...toStringArray(p.industry),
    ]),
    normalizeIndustry,
    'industries_include',
    dropped
  );
  if (indInc.length) out.industriesInclude = indInc;
  const indExc = normalizeArray(
    toStringArray(p.industries_exclude),
    normalizeIndustry,
    'industries_exclude',
    dropped
  );
  if (indExc.length) out.industriesExclude = indExc;

  // Headcount buckets. Accept the people-side ``company_sizes`` /
  // ``company_size`` spellings too — the model reuses them.
  const hcInc = normalizeArray(
    dedupe([
      ...toStringArray(p.headcount_ranges),
      ...toStringArray(p.company_sizes),
      ...toStringArray(p.company_size),
    ]),
    normalizeCompanyHeadcountRange,
    'headcount_ranges',
    dropped
  );
  if (hcInc.length) out.headcountRanges = hcInc;
  const hcExc = normalizeArray(
    toStringArray(p.headcount_ranges_exclude),
    normalizeCompanyHeadcountRange,
    'headcount_ranges_exclude',
    dropped
  );
  if (hcExc.length) out.headcountRangesExclude = hcExc;
  const hcMin = toNumericString(p.headcount_min);
  if (hcMin) out.headcountLatestMin = hcMin;
  const hcMax = toNumericString(p.headcount_max);
  if (hcMax) out.headcountLatestMax = hcMax;
  if (
    out.headcountLatestMin != null &&
    out.headcountLatestMax != null &&
    Number(out.headcountLatestMin) > Number(out.headcountLatestMax)
  ) {
    const tmp = out.headcountLatestMin;
    out.headcountLatestMin = out.headcountLatestMax;
    out.headcountLatestMax = tmp;
  }

  const ctInc = normalizeArray(
    toStringArray(p.company_types),
    normalizeCompanyType,
    'company_types',
    dropped
  );
  if (ctInc.length) out.companyType = ctInc;
  const ctExc = normalizeArray(
    toStringArray(p.company_types_exclude),
    normalizeCompanyType,
    'company_types_exclude',
    dropped
  );
  if (ctExc.length) out.companyTypeExclude = ctExc;

  // HQ country: accept the people-side ``company_countries`` spelling too.
  const hqInc = normalizeArray(
    dedupe([
      ...toStringArray(p.hq_countries),
      ...toStringArray(p.company_countries),
    ]),
    normalizeCountryIso3,
    'hq_countries',
    dropped
  );
  if (hqInc.length) out.hqCountriesIso3 = hqInc;
  const hqExc = normalizeArray(
    toStringArray(p.hq_countries_exclude),
    normalizeCountryIso3,
    'hq_countries_exclude',
    dropped
  );
  if (hqExc.length) out.hqCountriesIso3Exclude = hqExc;

  const states = dedupe(toStringArray(p.hq_states));
  if (states.length) out.hqStates = states;
  const cities = dedupe(toStringArray(p.hq_cities));
  if (cities.length) out.hqCities = cities;

  if (import.meta.env.DEV && dropped.length > 0) {
    console.warn(
      '[agent-filters] dropped company_search values that failed validation:',
      dropped
    );
  }

  return out;
}
